import api from "api";
import { frontDB } from "frontDB";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

export class TreeNode {
  constructor(
    id,
    name,
    fileId,
    filePath = null,
    parent = null,
    localStatus = "",
    cloudStatus = "",
    failedUploadAttempts = 0,
    failedDownloadAttempts = 0,
    actionLogs = [],
  ) {
    this.id = id;
    this.name = name;
    this.fileId = fileId;
    this.filePath = filePath;
    this.parent = parent;
    this.localStatus = localStatus;
    this.cloudStatus = cloudStatus;
    this.failedUploadAttempts = failedUploadAttempts;
    this.failedDownloadAttempts = failedDownloadAttempts;
    this.actionLogs = Array.isArray(actionLogs) ? actionLogs : [];
    this.children = [];
  }

  addChild(node) {
    node.parent = this;
    this.children.push(node);
  }

  removeChild(node) {
    this.children = this.children.filter((child) => child !== node);
  }
}

const { getLocationById, location } = frontDB.getState();

const initializeTree = (items, locationName) => {
  const root = new TreeNode("root", locationName);
  const itemMap = new Map();

  items.forEach((item) => {
    const node = new TreeNode(
      item._id,
      item.name,
      !!item.fileId,
      item.filePath,
      null,
      item.localStatus,
      item.cloudStatus,
      item.failedUploadAttempts,
      item.failedDownloadAttempts,
      item.actionLogs,
    );
    itemMap.set(item._id, node);
  });

  items.forEach((item) => {
    const node = itemMap.get(item._id);
    const parent = item.parentFolderId
      ? itemMap.get(item.parentFolderId)
      : root;
    parent.addChild(node);
  });

  return root;
};

export let useDocuments = create(
  immer((set, get) => ({
    locationId: undefined,
    currentNode: null,
    root: null,

    loading: false,
    setLoading: (loading) => {
      set({ loading });
    },
    setLocationId: (locationId) => {
      set({ locationId });
    },

    initialize: async () => {
      get().setLoading(true);
      let { locationId } = get();
      let response = await api.documents.getChildrenByIds(locationId, null);
      if (response) {
        const locationName = frontDB
          .getState()
          .locations.find((loc) => loc._id === locationId).name;
        const root = initializeTree(response, locationName);
        set({ root, currentNode: root });
      }
      get().setLoading(false);
    },

    handleRename: async (item, name) => {
      let response = await api.documents.renameItem(item.id, name);
      if (response) {
        item.name = name;
        set({}); //rerender
      }
    },

    handleDelete: async (item) => {
      get().setLoading(true);
      let response = await api.documents.deleteItem(item.id);
      if (response) {
        item.parent.removeChild(item);
        set({});
      }
      get().setLoading(false);
    },

    handleCreateFolder: async () => {
      get().setLoading(true);
      let { currentNode, locationId } = get();
      let folder = {
        name: `New Folder ${currentNode.children.length + 1}`,
        parentFolderId: currentNode.id === "root" ? null : currentNode.id,
        locationId: locationId,
        fileId: null,
      };
      let response = await api.documents.createFolder(folder);
      if (response) {
        const newNode = new TreeNode(
          response._id,
          response.name,
          !!response.fileId,
          response.filePath,
          null,
          response.localStatus,
          response.cloudStatus,
          response.failedUploadAttempts,
          response.failedDownloadAttempts,
          response.actionLogs,
        );
        currentNode.addChild(newNode);
        set({});
      }
      get().setLoading(false);
    },

    openFolder: async (node, forced = false) => {
      if (node.children.length > 0 && !forced) {
        set({ currentNode: node });
        return;
      } else {
        node.children = [];
      }
      get().setLoading(true);
      let response = await api.documents.getChildrenByIds(
        get().locationId,
        node.id === "root" ? null : node.id,
      );
      if (response) {
        response.forEach((item) => {
          const newNode = new TreeNode(
            item._id,
            item.name,
            item.fileId,
            item.filePath,
            null,
            item.localStatus,
            item.cloudStatus,
            item.failedUploadAttempts,
            item.failedDownloadAttempts,
            item.actionLogs,
          );
          node.addChild(newNode);
        });
        set({ currentNode: node });
      }
      get().setLoading(false);
    },

    navigateToPath: (node) => {
      set({ currentNode: node });
    },

    setSelectedLocation: () => {
      let { location } = frontDB.getState();
      if (!location) {
        set({ filter: { ...get().filter, locationId: "*" } });
        return;
      }

      set({ filter: { ...get().filter, locationId: location._id } });
    },

    locationHandler: (value) => {
      set({ locationId: value });
    },
  })),
);
